import { useMutation, useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import { queryClient } from '@lib/queryClient';
import type { ApiError } from '@/types/api.types';

import {
  getClosures,
  createClosure,
  patchClosure,
  deleteClosure,
} from '../api/closuresApi';
import type {
  Closure,
  ClosuresList,
  CreateClosureRequest,
  PatchClosureRequest,
} from '../types';

const closuresKey = (accountId: string) => ['settings', 'closures', accountId] as const;

export function useClosures() {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const query = useQuery({
    queryKey: closuresKey(accountId),
    queryFn: () => getClosures(accountId),
    enabled: !!accountId,
  });

  const createMutation = useMutation<Closure, ApiError, CreateClosureRequest>({
    mutationFn: (body) => createClosure(accountId, body),
    onSuccess: (created) => {
      queryClient.setQueryData<ClosuresList>(closuresKey(accountId), (prev) =>
        prev ? { closures: [...prev.closures, created] } : { closures: [created] },
      );
    },
  });

  const patchMutation = useMutation<
    Closure,
    ApiError,
    { closureId: string; body: PatchClosureRequest }
  >({
    mutationFn: ({ closureId, body }) => patchClosure(accountId, closureId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<ClosuresList>(closuresKey(accountId), (prev) =>
        prev
          ? {
              closures: prev.closures.map((c) =>
                c.closureId === updated.closureId ? updated : c,
              ),
            }
          : prev,
      );
    },
  });

  const deleteMutation = useMutation<void, ApiError, string>({
    mutationFn: (closureId) => deleteClosure(accountId, closureId),
    onSuccess: (_, closureId) => {
      queryClient.setQueryData<ClosuresList>(closuresKey(accountId), (prev) =>
        prev
          ? { closures: prev.closures.filter((c) => c.closureId !== closureId) }
          : prev,
      );
    },
  });

  return {
    closures: query.data?.closures ?? [],
    isLoading: query.isLoading,
    fetchError: query.error as ApiError | null,
    create: createMutation.mutate,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    patch: patchMutation.mutate,
    isPatching: patchMutation.isPending,
    patchError: patchMutation.error,
    remove: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  };
}
